// MarketCardIcon Component - Extracted to avoid function calls in JSX
import { Box } from '@mui/material';

import { colors } from '@/styles/theme';
import type { MarketIcon } from '@/types';

interface MarketCardIconProps {
  icon?: MarketIcon;
}

const ICON_MAP: Record<MarketIcon, React.ReactNode> = {
  sports: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill="#1f9d63" />
      <circle cx="16" cy="16" r="8" fill="none" stroke="#fff" strokeWidth="2" />
      <path d="M8 16H24" stroke="#fff" strokeWidth="2" />
      <path d="M16 8V24" stroke="#fff" strokeWidth="2" />
    </svg>
  ),
  portugal: <span className="market-icon-flag">🇵🇹</span>,
  'presidential-seal': (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill={colors.info.dark} stroke="#e8b046" strokeWidth="1.5" />
      <path d="M16 8L18 14L24 14L19 17L21 23L16 19L11 23L13 17L8 14L14 14L16 8Z" fill="#e8b046" />
    </svg>
  ),
  democratic: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill={colors.info.main} />
      <path
        d="M12 20C12 18 14 16 16 16C18 16 20 18 20 20"
        stroke="#fff"
        strokeWidth="2"
        fill="none"
      />
      <circle cx="14" cy="14" r="1.5" fill="#fff" />
      <circle cx="18" cy="14" r="1.5" fill="#fff" />
    </svg>
  ),
  republican: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill="#c24632" />
      <path d="M16 10L20 18L12 18L16 10Z" fill="#fff" />
      <rect x="14" y="18" width="4" height="6" fill="#fff" />
    </svg>
  ),
  powell: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill={colors.neutral[500]} />
      <circle cx="16" cy="14" r="4" fill="#fff" />
      <path
        d="M10 22C10 19 12 17 16 17C20 17 22 19 22 22"
        stroke="#fff"
        strokeWidth="2"
        fill="none"
      />
    </svg>
  ),
  musk: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill={colors.neutral[800]} />
      <circle cx="16" cy="14" r="4" fill="#fff" />
      <path
        d="M10 22C10 19 12 17 16 17C20 17 22 19 22 22"
        stroke="#fff"
        strokeWidth="2"
        fill="none"
      />
    </svg>
  ),
  chile: <span className="market-icon-flag">🇨🇱</span>,
  time: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="2" fill="#c24632" />
      <text x="16" y="20" fontSize="12" fontWeight="bold" fill="#fff" textAnchor="middle">
        TIME
      </text>
    </svg>
  ),
  // Tech icons
  gemini: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill={colors.brand.google.blue} />
      <path
        d="M12 10L16 8L20 10L22 14L20 18L16 20L12 18L10 14L12 10Z"
        fill="#fff"
        stroke={colors.brand.google.blue}
        strokeWidth="1"
      />
    </svg>
  ),
  ai: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill="#7c3aed" />
      <circle cx="12" cy="14" r="2" fill="#fff" />
      <circle cx="20" cy="14" r="2" fill="#fff" />
      <path
        d="M12 20C12 18 14 17 16 17C18 17 20 18 20 20"
        stroke="#fff"
        strokeWidth="2"
        fill="none"
      />
    </svg>
  ),
  code: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="8" width="24" height="16" rx="2" fill="#211d18" />
      <path
        d="M12 14L10 16L12 18"
        stroke="#4ec38a"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20 14L22 16L20 18"
        stroke="#4ec38a"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M15 12L17 20" stroke="#4ec38a" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  draftkings: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill="#53d337" />
      <text x="16" y="20" fontSize="10" fontWeight="bold" fill="#000" textAnchor="middle">
        DK
      </text>
    </svg>
  ),
  stocks: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill="#059669" />
      <path
        d="M10 18L14 14L18 16L22 12"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19 12H22V15"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  tesla: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill="#cc0000" />
      <text x="16" y="20" fontSize="10" fontWeight="bold" fill="#fff" textAnchor="middle">
        T
      </text>
    </svg>
  ),
  ipo: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill="#0284c7" />
      <path d="M12 12H20V20H12V12Z" stroke="#fff" strokeWidth="2" fill="none" />
      <circle cx="16" cy="16" r="2" fill="#fff" />
    </svg>
  ),
  elon: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill={colors.info.dark} />
      <circle cx="16" cy="14" r="4" fill="#fff" />
      <path
        d="M10 22C10 19 12 17 16 17C20 17 22 19 22 22"
        stroke="#fff"
        strokeWidth="2"
        fill="none"
      />
    </svg>
  ),
  tiktok: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="6" fill="#000000" />
      <circle cx="14" cy="16" r="3" fill="#00f2ea" />
      <circle cx="18" cy="12" r="3" fill="#ff0050" />
    </svg>
  ),
  x: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill="#000000" />
      <text x="16" y="21" fontSize="14" fontWeight="bold" fill="#fff" textAnchor="middle">
        𝕏
      </text>
    </svg>
  ),
  // Culture icons
  movie: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="6" width="24" height="20" rx="2" fill="#c24632" />
      <rect x="6" y="8" width="20" height="16" fill={colors.neutral[800]} />
      <path d="M10 16L14 13V19L10 16Z" fill="#fff" />
    </svg>
  ),
  poker: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="8" width="24" height="16" rx="2" fill={colors.neutral[800]} />
      <circle cx="12" cy="12" r="2" fill="#c24632" />
      <circle cx="20" cy="12" r="2" fill="#000" />
      <circle cx="12" cy="20" r="2" fill="#000" />
      <circle cx="20" cy="20" r="2" fill="#c24632" />
    </svg>
  ),
  santa: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill="#c24632" />
      <circle cx="16" cy="15" r="5" fill="#fff" />
      <rect x="13" y="18" width="6" height="8" fill="#fff" />
      <circle cx="16" cy="10" r="3" fill="#c24632" />
    </svg>
  ),
  donation: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill="#059669" />
      <path
        d="M12 16C12 14 14 13 16 13C18 13 20 14 20 16C20 18 18 19 16 21C14 19 12 18 12 16Z"
        fill="#fff"
      />
    </svg>
  ),
  mrbeast: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="6" fill="#000000" />
      <circle cx="12" cy="14" r="2" fill="#059669" />
      <circle cx="20" cy="14" r="2" fill="#059669" />
      <path d="M11 19H21" stroke="#059669" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  wealth: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill="#e8b046" />
      <text x="16" y="21" fontSize="16" fontWeight="bold" fill="#fff" textAnchor="middle">
        $
      </text>
    </svg>
  ),
  steam: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill="#211d18" />
      <circle cx="16" cy="12" r="4" fill="#83a8ff" />
      <path d="M16 16L10 22" stroke="#83a8ff" strokeWidth="2" strokeLinecap="round" />
      <circle cx="10" cy="22" r="2" fill="#83a8ff" />
    </svg>
  ),
  // World icons
  japan: <span className="market-icon-flag">🇯🇵</span>,
  china: <span className="market-icon-flag">🇨🇳</span>,
  brazil: <span className="market-icon-flag">🇧🇷</span>,
  syria: <span className="market-icon-flag">🇸🇾</span>,
  'china-taiwan': (
    <Box className="market-icon-flags">
      <span className="market-icon-flag">🇨🇳</span>
      <span className="market-icon-flag">🇹🇼</span>
    </Box>
  ),
  'thailand-cambodia': (
    <Box className="market-icon-flags">
      <span className="market-icon-flag">🇹🇭</span>
      <span className="market-icon-flag">🇰🇭</span>
    </Box>
  ),
  'us-syria': (
    <Box className="market-icon-flags">
      <span className="market-icon-flag">🇺🇸</span>
      <span className="market-icon-flag">🇸🇾</span>
    </Box>
  ),
  // Economy icons
  gold: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill="#e8b046" />
      <circle cx="16" cy="16" r="8" fill="#e8b046" />
      <circle cx="16" cy="16" r="4" fill="#e8b046" />
    </svg>
  ),
  ecb: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="12" fill="#003399" />
      <circle cx="10" cy="12" r="1.5" fill="#ffcc00" />
      <circle cx="14" cy="10" r="1.5" fill="#ffcc00" />
      <circle cx="18" cy="10" r="1.5" fill="#ffcc00" />
      <circle cx="22" cy="12" r="1.5" fill="#ffcc00" />
      <circle cx="10" cy="20" r="1.5" fill="#ffcc00" />
      <circle cx="14" cy="22" r="1.5" fill="#ffcc00" />
      <circle cx="18" cy="22" r="1.5" fill="#ffcc00" />
      <circle cx="22" cy="20" r="1.5" fill="#ffcc00" />
    </svg>
  ),
  bitcoin: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill="#f7931a" />
      <text x="16" y="21" fontSize="16" fontWeight="bold" fill="#fff" textAnchor="middle">
        ₿
      </text>
    </svg>
  ),
  ethereum: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <path d="M16 4L8 16L16 20L24 16L16 4Z" fill="#627EEA" />
      <path d="M8 16L16 20L16 28L8 22L8 16Z" fill="#627EEA" />
      <path d="M16 28L24 22L24 16L16 20L16 28Z" fill="#627EEA" />
    </svg>
  ),
  inflation: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill="#c24632" />
      <path
        d="M12 18L16 12L20 18"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M16 12V22" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  recession: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill="#c24632" />
      <path
        d="M10 12L16 18L22 12"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  treasury: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="2" fill={colors.info.dark} />
      <rect x="8" y="10" width="16" height="2" fill="#fff" />
      <rect x="8" y="15" width="16" height="2" fill="#fff" />
      <rect x="8" y="20" width="16" height="2" fill="#fff" />
    </svg>
  ),
  // Company logos - Matching screenshot design
  netflix: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill={colors.brand.netflix} />
      <text
        x="16"
        y="21"
        fontSize="20"
        fontWeight="900"
        fill="#fff"
        textAnchor="middle"
        fontFamily="Arial Black, sans-serif"
        letterSpacing="-0.05em"
      >
        N
      </text>
    </svg>
  ),
  microsoft: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="5" y="5" width="10" height="10" fill="#F25022" />
      <rect x="17" y="5" width="10" height="10" fill="#7FBA00" />
      <rect x="5" y="17" width="10" height="10" fill="#00A4EF" />
      <rect x="17" y="17" width="10" height="10" fill="#FFB900" />
    </svg>
  ),
  nvidia: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill="#76B900" />
      <text
        x="16"
        y="20"
        fontSize="11"
        fontWeight="700"
        fill="#fff"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        letterSpacing="0.05em"
      >
        NVDA
      </text>
    </svg>
  ),
  meta: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill="#0081FB" />
      <path
        d="M12 16C12 14 13.5 12.5 16 12.5C18.5 12.5 20 14 20 16C20 18 18.5 19.5 16 19.5C13.5 19.5 12 18 12 16Z"
        fill="#fff"
      />
      <path
        d="M8 16C8 14.5 9.5 13 12 13C14.5 13 16 14.5 16 16"
        stroke="#fff"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M24 16C24 14.5 22.5 13 20 13C17.5 13 16 14.5 16 16"
        stroke="#fff"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  ),
  amazon: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill="#FF9900" />
      <text
        x="16"
        y="20"
        fontSize="18"
        fontWeight="bold"
        fill="#fff"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
      >
        a
      </text>
    </svg>
  ),
  apple: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill="#000000" />
      <path
        d="M16 9C15.5 9 15 9.3 15 9.8C15 10.3 15.5 10.6 16 10.6C16.5 10.6 17 10.3 17 9.8C17 9.3 16.5 9 16 9Z"
        fill="#fff"
      />
      <path
        d="M13 13C12.5 13 12 13.4 12 14C12 14.6 12.5 15 13 15C13.5 15 14 14.6 14 14C14 13.4 13.5 13 13 13Z"
        fill="#fff"
      />
      <path
        d="M19 13C18.5 13 18 13.4 18 14C18 14.6 18.5 15 19 15C19.5 15 20 14.6 20 14C20 13.4 19.5 13 19 13Z"
        fill="#fff"
      />
      <path
        d="M16 19C15.5 19 15 19.4 15 20C15 20.6 15.5 21 16 21C16.5 21 17 20.6 17 20C17 19.4 16.5 19 16 19Z"
        fill="#fff"
      />
    </svg>
  ),
  google: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill={colors.brand.google.blue} />
      <text
        x="16"
        y="20"
        fontSize="18"
        fontWeight="bold"
        fill="#fff"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
      >
        G
      </text>
    </svg>
  ),
  palantir: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill="#000000" />
      <text
        x="16"
        y="20"
        fontSize="18"
        fontWeight="bold"
        fill="#fff"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
      >
        P
      </text>
    </svg>
  ),
  opendoor: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill="#00A862" />
      <text
        x="16"
        y="20"
        fontSize="18"
        fontWeight="bold"
        fill="#fff"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
      >
        O
      </text>
    </svg>
  ),
  wd40: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill="#FFD700" />
      <text
        x="16"
        y="20"
        fontSize="9"
        fontWeight="bold"
        fill="#000"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        letterSpacing="0.05em"
      >
        WD-40
      </text>
    </svg>
  ),
  clinton: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill="#0ea5e9" />
      <circle cx="16" cy="14" r="4" fill="#fff" />
      <path
        d="M10 22C10 19 12 17 16 17C20 17 22 19 22 22"
        stroke="#fff"
        strokeWidth="2"
        fill="none"
      />
    </svg>
  ),
  'oil-tanker': (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="12" width="24" height="12" rx="2" fill={colors.neutral[500]} />
      <rect x="6" y="14" width="20" height="8" fill={colors.neutral[700]} />
      <circle cx="10" cy="20" r="2" fill={colors.neutral[800]} />
      <circle cx="22" cy="20" r="2" fill={colors.neutral[800]} />
    </svg>
  ),
  'military-aircraft': (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <path
        d="M16 8L20 12L24 10L22 16L24 22L20 20L16 24L12 20L8 22L10 16L8 10L12 12L16 8Z"
        fill={colors.neutral[500]}
      />
    </svg>
  ),
  'military-tank': (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="6" y="14" width="20" height="10" rx="2" fill={colors.neutral[500]} />
      <rect x="8" y="16" width="16" height="6" fill={colors.neutral[700]} />
      <circle cx="10" cy="21" r="2" fill={colors.neutral[800]} />
      <circle cx="22" cy="21" r="2" fill={colors.neutral[800]} />
      <rect x="12" y="10" width="8" height="6" fill={colors.neutral[600]} />
    </svg>
  ),
  nato: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill="#0066CC" />
      <path
        d="M16 8L20 12L24 10L22 16L24 22L20 20L16 24L12 20L8 22L10 16L8 10L12 12L16 8Z"
        fill="#fff"
      />
    </svg>
  ),
  'peace-dove': (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill={colors.neutral[200]} />
      <path
        d="M12 14C12 12 14 11 16 11C18 11 20 12 20 14C20 16 18 17 16 19C14 17 12 16 12 14Z"
        fill={colors.neutral[500]}
      />
      <path d="M14 18L18 22" stroke={colors.neutral[500]} strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  'gold-card': (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="8" width="24" height="16" rx="2" fill="#FFD700" />
      <rect x="6" y="10" width="20" height="12" fill="#FFA500" />
      <text x="16" y="20" fontSize="10" fontWeight="bold" fill="#000" textAnchor="middle">
        GOLD
      </text>
    </svg>
  ),
  'cargo-ship': (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="16" width="24" height="8" rx="1" fill={colors.neutral[500]} />
      <rect x="6" y="18" width="20" height="4" fill={colors.neutral[700]} />
      <rect x="8" y="12" width="16" height="6" fill={colors.neutral[600]} />
      <circle cx="10" cy="22" r="1.5" fill={colors.neutral[800]} />
      <circle cx="22" cy="22" r="1.5" fill={colors.neutral[800]} />
    </svg>
  ),
  dollar: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill="#059669" />
      <text x="16" y="21" fontSize="16" fontWeight="bold" fill="#fff" textAnchor="middle">
        $
      </text>
    </svg>
  ),
  flags: (
    <Box className="market-icon-flags">
      <span className="market-icon-flag">🇺🇸</span>
      <span className="market-icon-flag">🇨🇳</span>
    </Box>
  ),
  globe: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill={colors.info.main} />
      <path d="M8 16H24" stroke="#fff" strokeWidth="1.5" />
      <path
        d="M16 8C18 8 20 10 20 12C20 14 18 16 16 18C14 18 12 16 12 14C12 12 14 10 16 8Z"
        stroke="#fff"
        strokeWidth="1.5"
        fill="none"
      />
    </svg>
  ),
  'lisa-cook': (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14" fill="#7c3aed" />
      <circle cx="16" cy="14" r="4" fill="#fff" />
      <path
        d="M10 22C10 19 12 17 16 17C20 17 22 19 22 22"
        stroke="#fff"
        strokeWidth="2"
        fill="none"
      />
    </svg>
  ),
  'us-flag': <span className="market-icon-flag">🇺🇸</span>,
  capitol: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="8" y="18" width="16" height="8" fill={colors.neutral[500]} />
      <path d="M8 18L16 10L24 18H8Z" fill="#c24632" />
      <rect x="12" y="20" width="8" height="6" fill={colors.neutral[700]} />
      <circle cx="16" cy="12" r="2" fill="#e8b046" />
    </svg>
  ),
  // Best of 2025 icons
  'box-office': (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="6" width="24" height="20" rx="2" fill={colors.neutral[800]} />
      <rect x="6" y="8" width="20" height="16" fill={colors.neutral[900]} />
      <text
        x="16"
        y="20"
        fontSize="8"
        fontWeight="bold"
        fill="#fff"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        letterSpacing="0.1em"
      >
        BOX OFFICE
      </text>
    </svg>
  ),
  openai: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill="#10A37F" />
      <path d="M12 10L16 14L20 10L20 18L16 14L12 18L12 10Z" fill="#fff" />
    </svg>
  ),
  anthropic: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill="#D4A574" />
      <path d="M12 10L16 14L20 10L20 18L16 14L12 18L12 10Z" fill="#fff" />
    </svg>
  ),
  elections: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="6" y="10" width="20" height="16" rx="2" fill={colors.info.dark} />
      <path
        d="M12 10V8C12 6.89543 12.8954 6 14 6H18C19.1046 6 20 6.89543 20 8V10"
        stroke={colors.info.dark}
        strokeWidth="2"
      />
      <rect x="14" y="14" width="4" height="2" fill="#fff" />
    </svg>
  ),
  'popular-names': (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="4" fill={colors.neutral[100]} />
      <text
        x="16"
        y="14"
        fontSize="8"
        fontWeight="bold"
        fill={colors.neutral[800]}
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
      >
        Liam
      </text>
      <text
        x="16"
        y="22"
        fontSize="8"
        fontWeight="bold"
        fill={colors.neutral[500]}
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
      >
        Emma
      </text>
    </svg>
  ),
  minecraft: (
    <svg className="market-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="2" fill="#000000" />
      <rect x="6" y="6" width="10" height="10" fill="#7CB342" />
      <rect x="16" y="6" width="10" height="10" fill="#8BC34A" />
      <rect x="6" y="16" width="10" height="10" fill="#558B2F" />
      <rect x="16" y="16" width="10" height="10" fill="#689F38" />
      <text
        x="16"
        y="22"
        fontSize="6"
        fontWeight="bold"
        fill="#fff"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
      >
        MINECRAFT
      </text>
    </svg>
  ),
};

export function MarketCardIcon({ icon }: MarketCardIconProps) {
  if (!icon) {
    return (
      <Box
        sx={{ fontSize: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        📊
      </Box>
    );
  }

  const iconElement = ICON_MAP[icon];

  if (!iconElement) {
    return (
      <Box
        sx={{ fontSize: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        📊
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {iconElement}
    </Box>
  );
}

export default MarketCardIcon;
